import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Routes from 'lib/utils/routes';
import { throwErrorToast, throwSuccessToast } from 'lib/utils/toast';
import { createActivitySetInput } from 'lib/utils/activities';
import { TEMP_REFS } from 'lib/constants';

import { CREATE_ACTIVITY_SET_WITH_WORK_ORDER } from 'lib/api/mutations';
import {
  actionCanonicalActivitySetOpen,
  actionCanonicalActivitySetDelete,
  actionOptimisticActivitySetOpen,
  actionSetJobTotal,
  actionSetWorkOrderOpActionLoading,
  actionSetPendingActivityTypeRef,
} from 'lib/actions';
import { getLatestJob } from 'lib/selectors/getLatestJob';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { useIsLaborTicketFlow } from 'lib/hooks/useIsLaborTicketFlow';
import useStartActivityWithLaborTicket from './useStartActivityWithLaborTicket';

const {
  ACTIVITY_REF: optimisticActivityRef,
  ACTIVITY_SET_REF: optimisticActivitySetRef,
} = TEMP_REFS;

const useCreateActivitySetWithWorkOrder = ({ machine, workOrderOp }) => {
  const mainDispatch = useDispatch();
  const history = useHistory();
  const laborTicketEnabled = useIsLaborTicketFlow();

  const workOrderId = workOrderOp?.workOrderId;

  const job = useSelector(getLatestJob);
  const latestActivitySet = useSelector(getLatestActivitySet);

  const requestStartActivityWithLaborTicket = useStartActivityWithLaborTicket({
    machine,
    workOrderOp,
  });

  const [requestCreateActivitySetWithWorkOrder] = useMutation(
    CREATE_ACTIVITY_SET_WITH_WORK_ORDER,
    {
      fetchPolicy: 'no-cache',
      update: (
        _cache,
        {
          data: {
            createActivitySetWithWorkOrderResponse: {
              activitySet: {
                activities: [activity],
                ...activitySet
              },
            },
          },
        }
      ) => {
        mainDispatch(
          actionOptimisticActivitySetOpen({ activitySet, activity, job })
        );
        mainDispatch(actionSetJobTotal(undefined));
        history.push(Routes.machineIdPath(machine.id));
      },
      onCompleted: async ({
        createActivitySetWithWorkOrderResponse: {
          activitySet: {
            activities: [activity], // should be sorted start ASC
            ...activitySet
          },
        },
      }) => {
        await mainDispatch(
          actionCanonicalActivitySetOpen({
            activitySet,
            activity,
            optimisticActivitySetRef,
            optimisticActivityRef,
          })
        );

        throwSuccessToast(`Started order ${activitySet.workOrderId}`);
        mainDispatch(actionSetWorkOrderOpActionLoading(false));
        mainDispatch(actionSetPendingActivityTypeRef(null));
        history.push(Routes.machineIdPath(machine.id));
      },
      onError: () => {
        mainDispatch(
          actionCanonicalActivitySetDelete({
            activitySetRef: optimisticActivitySetRef,
          })
        );
        mainDispatch(actionSetWorkOrderOpActionLoading(false));
        mainDispatch(actionSetPendingActivityTypeRef(null));
        throwErrorToast('Could not start production order');
      },
    }
  );

  return async ({
    activityTypeRef,
    partOperation,
    startAt,
    workOrderOp: callTimeWorkOrderOp,
  }) => {
    // Use call-time workOrderOp if provided, otherwise fall back to initialization value
    const effectiveWorkOrderOp = callTimeWorkOrderOp || workOrderOp;
    const effectiveWorkOrderId =
      effectiveWorkOrderOp?.workOrderId || workOrderId;

    if (partOperation && laborTicketEnabled && effectiveWorkOrderOp) {
      await requestStartActivityWithLaborTicket({
        activityTypeRef,
        partOperation,
        startAt,
      });
    } else if (partOperation) {
      const activitySetInput = createActivitySetInput({
        machine,
        workOrderOp: effectiveWorkOrderOp,
        partOperation,
        activityTypeRef,
        startAt,
      });

      await requestCreateActivitySetWithWorkOrder({
        variables: {
          input: activitySetInput,
          query: { shouldCloseLatestSet: true },
        },

        optimisticResponse: {
          __typename: 'Mutation',
          createActivitySetWithWorkOrderResponse: {
            __typename: 'ActivitySet',
            activitySet: {
              ...latestActivitySet,
              workOrderId: effectiveWorkOrderId,
            },
          },
        },
      });
    }
  };
};

export default useCreateActivitySetWithWorkOrder;
